import React, { useEffect, useContext } from "react";
import { useStore } from "zustand";
import GlobalContext from "../../../context/global";
import {
  errorService,
  leadsService,
  VehicleOfInterestService,
} from "../../../../services";
import {
  useAppStateStore,
  useLeadStore,
  useVehicleOfInterestStore,
} from "../../../../stores";
import { Dropdown } from "../index";
import { range } from "../../../../utilities";
import { validationPropsVehicleOfInterest } from "./validationProps";
import { usePrevious } from "../../../hooks";

const vehicleOfInterestService = new VehicleOfInterestService();

const handleYear = (year, modelName) => {
  let availableYear = year;
  const { modelData } = vehicleOfInterestService.get();

  const setYear = (year) => {
    vehicleOfInterestService.setSelectedWithoutResetting({ year });
    leadsService.setVehicleOfInterest({ year });
  };

  if (modelData) {
    modelData.filter((item) => {
      if (item.ModelName === modelName) availableYear = item.Year;
    });
    if (availableYear !== year) setYear(availableYear);
  }
};

const getMakes = async (year) => {
  const lead = useLeadStore.get();
  if (!year) return;
  try {
    await vehicleOfInterestService.getMakes(year);
  } catch (error) {
    errorService.handle(error, lead, errorService.ERROR_TYPES.recoverable);
  }
};

const getNewMakes = async (year, vehicleState) => {
  if (!year) return;
  const makesFunction =
    vehicleState === "used" ? "getUsedMakes" : "getNewMakes";
  try {
    if (!(await vehicleOfInterestService[makesFunction](year))) {
      await getMakes(year);
    }
    leadsService.setVehicleOfInterest({ carType: vehicleState });
  } catch (error) {
    errorService.handle(error, lead, errorService.ERROR_TYPES.recoverable);
  }
};

const getModels = async (make) => {
  const lead = useLeadStore.get();
  const { year } = lead.vehicleOfInterest;
  if (!year || !make) return;
  try {
    await vehicleOfInterestService.getModels(year, make);
  } catch (error) {
    errorService.handle(error, lead, errorService.ERROR_TYPES.recoverable);
  }
};

const getSeries = async (model) => {
  const lead = useLeadStore.get();
  const { year, make } = lead.vehicleOfInterest;
  if (!year || !make || !model) return;
  try {
    await vehicleOfInterestService.getSeries(year, make, model);
  } catch (error) {
    errorService.handle(error, lead, errorService.ERROR_TYPES.recoverable);
  }
};

const getStyles = async (series) => {
  const lead = useLeadStore.get();
  const { year, make, model } = lead.vehicleOfInterest;
  if (!year || !make || !model || !series) return;
  try {
    await vehicleOfInterestService.getStyles(year, make, model, series);
  } catch (error) {
    errorService.handle(error, lead, errorService.ERROR_TYPES.recoverable);
  }
};

const setStyle = async (style) => {
  vehicleOfInterestService.setSelected({ style });
  leadsService.setVehicleOfInterest({ style });
};

const getVDATMakes = async (type = "new") => {
  const lead = useLeadStore.get();
  vehicleOfInterestService.set({ vehicleState: type });
  try {
    await vehicleOfInterestService.getVDATMakesModels();
  } catch (error) {
    errorService.handle(error, lead, errorService.ERROR_TYPES.recoverable);
  }
};

const getVDATModels = (make) => {
  if (!make) return;
  vehicleOfInterestService.getVDATModels(make);
};

const getCarType = (vehicleState) => {
  return { new: 1, voiNotNow: 1, used: 2 }[vehicleState] || 1;
};

const VoiYear = () => {
  let voiUsedMakesNumberOfYearsPrevious =
    vehicleOfInterestService.getNumberOfYearsPrevious();
  const appData = useContext(GlobalContext);
  const { currentYear } = appData;
  const years = range(
    currentYear,
    currentYear - voiUsedMakesNumberOfYearsPrevious,
  );
  const appState = useStore(useAppStateStore);
  const lead = useStore(useLeadStore);

  const validateYear = () => {
    const { year } = validationPropsVehicleOfInterest("year");
    useAppStateStore.setValidity("aboutYou", { voiYear: !!year });
  };

  useEffect(() => {
    validateYear();
  }, [lead.vehicleOfInterest.year]);

  return (
    <Dropdown
      id="year"
      dataTestId="voi-year"
      label="Year"
      onChange={(e) => (async () => await getMakes(parseInt(e.target.value)))()}
      required={true}
      hasError={appState.formSubmitted && !appState.isValid.aboutYou.voiYear}
      value={lead.vehicleOfInterest.year}
      data={years}
      errorText="Please select a make"
    />
  );
};

const VoiMake = () => {
  const appData = useContext(GlobalContext);
  const appState = useStore(useAppStateStore);
  const lead = useStore(useLeadStore);
  const previousLead = usePrevious(lead);
  const vehicleOfInterest = useStore(useVehicleOfInterestStore);
  const { isCDTT } = appData;

  const validateMake = () => {
    const { make } = validationPropsVehicleOfInterest("make");
    useAppStateStore.setValidity("aboutYou", { voiMake: !!make });
  };

  const loadMakes = () => {
    if (isCDTT) {
      (async () => await getVDATMakes(vehicleOfInterest?.vehicleState))();
    } else {
      (async () => {
        await getNewMakes(
          lead?.vehicleOfInterest?.year,
          vehicleOfInterest.vehicleState,
        );
      })();
    }
  };

  const handleOnChange = (event) => {
    const make = event.target.value;
    if (isCDTT) {
      getVDATModels(make);
    } else {
      (async () => await getModels(make))();
    }
  };

  useEffect(() => {
    if (
      lead.vehicleOfInterest?.year !== previousLead?.vehicleOfInterest?.year ||
      (lead.vehicleOfInterest?.year && !vehicleOfInterest.makes.length)
    ) {
      if (
        vehicleOfInterest.models.length === 0 ||
        previousLead?.vehicleOfInterest.year === undefined
      ) {
        loadMakes();
      }
    }
  }, [lead.vehicleOfInterest.year]);

  useEffect(() => {
    validateMake();
  }, [lead.vehicleOfInterest.make]);

  return (
    <Dropdown
      id="make"
      dataTestId="voi-make"
      label="Make"
      onChange={handleOnChange}
      disabled={!vehicleOfInterest.makes || !vehicleOfInterest.makes.length}
      required={true}
      hasError={appState.formSubmitted && !appState.isValid.aboutYou.voiMake}
      value={lead.vehicleOfInterest.make}
      data={vehicleOfInterest.makes}
      errorText="Please select a make"
      displayRequirementIndicator={true}
    />
  );
};

const VoiModel = () => {
  const appState = useStore(useAppStateStore);
  const lead = useStore(useLeadStore);
  const previousLead = usePrevious(lead);
  const vehicleOfInterest = useStore(useVehicleOfInterestStore);
  const appData = useContext(GlobalContext);
  const { currentYear, isCDTT, ClientApi } = appData;

  const validateModel = () => {
    const { model } = validationPropsVehicleOfInterest("model");
    useAppStateStore.setValidity("aboutYou", { voiModel: !!model });
  };

  useEffect(() => {
    if (
      lead.vehicleOfInterest?.make !== previousLead?.vehicleOfInterest?.make ||
      (lead.vehicleOfInterest?.make && !vehicleOfInterest.models.length)
    )
      if (isCDTT) {
        getVDATModels(lead?.vehicleOfInterest?.make);
      } else {
        (async () => {
          await getModels(lead?.vehicleOfInterest?.make);
        })();
      }
  }, [lead.vehicleOfInterest.make]);

  useEffect(() => {
    validateModel();
    leadsService.setVehicleOfInterest({
      carType: vehicleOfInterestService.get("vehicleState"),
    });
    ClientApi.update(
      {
        eventName: "elEngage",
        data: [
          { element: "voiModel", vehicleOfInterest: lead.vehicleOfInterest },
        ],
      },
      "aboutYou",
    );
  }, [lead.vehicleOfInterest.model]);

  return (
    <Dropdown
      id="model"
      dataTestId="voi-model"
      label="Model"
      onChange={(e) => {
        const modelName = e.target.value;
        (async () => {
          if (vehicleOfInterest.vehicleState === "new") {
            handleYear(currentYear, modelName);
          }
          await getSeries(modelName);
        })();
      }}
      disabled={
        !lead.vehicleOfInterest.make ||
        !vehicleOfInterest.models ||
        !vehicleOfInterest.models.length
      }
      required={true}
      hasError={appState.formSubmitted && !appState.isValid.aboutYou.voiModel}
      value={lead.vehicleOfInterest.model}
      data={vehicleOfInterest.models}
      errorText="Please select a model"
      displayRequirementIndicator={true}
    />
  );
};

const VoiSeries = () => {
  const appState = useStore(useAppStateStore);
  const lead = useStore(useLeadStore);
  const vehicleOfInterest = useStore(useVehicleOfInterestStore);

  const validateSeries = () => {
    const { series } = validationPropsVehicleOfInterest("series");
    useAppStateStore.setValidity("aboutYou", { voiSeries: !!series });
  };

  useEffect(() => {
    (async () => await getStyles(lead.vehicleOfInterest.series))();
  }, [lead.vehicleOfInterest.model]);

  useEffect(() => {
    validateSeries();
  }, [lead.vehicleOfInterest.series]);

  return (
    <Dropdown
      id="series"
      dataTestId="voi-series"
      label="Series"
      onChange={(e) => (async () => await getStyles(e.target.value))()}
      disabled={
        !lead.vehicleOfInterest.model ||
        !vehicleOfInterest.series ||
        !vehicleOfInterest.series.length
      }
      required={true}
      hasError={appState.formSubmitted && !appState.isValid.aboutYou.voiSeries}
      value={lead.vehicleOfInterest.series}
      data={vehicleOfInterest.series}
      errorText="Please select a model"
      displayRequirementIndicator={true}
    />
  );
};

const VoiStyle = () => {
  const appState = useStore(useAppStateStore);
  const lead = useStore(useLeadStore);
  const vehicleOfInterest = useStore(useVehicleOfInterestStore);
  const previousLead = usePrevious(lead);

  const validateStyle = () => {
    const { style } = validationPropsVehicleOfInterest("style");
    useAppStateStore.setValidity("aboutYou", { voiStyle: !!style });
  };

  useEffect(() => {
    if (
      previousLead?.vehicleOfInterest?.series !==
      lead?.vehicleOfInterest?.series
    )
      (async () => await getStyles(lead?.vehicleOfInterest?.series))();
  }, [lead.vehicleOfInterest.series]);

  useEffect(() => {
    validateStyle();
  }, [lead.vehicleOfInterest.style]);

  return (
    <Dropdown
      id="style"
      dataTestId="voi-style"
      label="Style"
      onChange={(e) => setStyle(e.target.value)}
      disabled={
        !lead.vehicleOfInterest.series ||
        !vehicleOfInterest.styles ||
        !vehicleOfInterest.styles.length
      }
      required={true}
      hasError={appState.formSubmitted && !appState.isValid.aboutYou.voiStyle}
      value={lead.vehicleOfInterest.style}
      data={vehicleOfInterest.styles}
      errorText="Please select a style"
      displayRequirementIndicator={true}
    />
  );
};

const InfoVoi = () => {
  const appData = useContext(GlobalContext);
  const { currentYear, isCDTT } = appData;
  const lead = useStore(useLeadStore);
  const vehicleOfInterest = useStore(useVehicleOfInterestStore);

  useEffect(() => {
    leadsService.set({
      vehicleOfInterest: vehicleOfInterest.selected,
      carType: getCarType(vehicleOfInterest.vehicleState),
    });
  }, [vehicleOfInterest.selected, vehicleOfInterest.vehicleState]);

  useEffect(() => {
    if (!lead.vehicleOfInterest.year)
      leadsService.setVehicleOfInterest({ year: currentYear });
  }, []);

  return (
    <div className="row">
      {!isCDTT && vehicleOfInterest.vehicleState === "used" && <VoiYear />}
      <VoiMake />
      <VoiModel />
      {!isCDTT && vehicleOfInterestService.showSeriesAndStyle && (
        <>
          <VoiSeries />
          <VoiStyle />
        </>
      )}
    </div>
  );
};

export default InfoVoi;
export { getVDATMakes };
